import { INovaEntity } from './base';
import { formatPhoneNumber } from './js-helpers';
import type { Maybe, Nullable } from './globals';

export interface IMessageThread extends INovaEntity {
  orgId: string;
  assetVisitId: string;
  messages?: IMessageThreadMessage[];
  expiresAt: Date;
  usesWhatsApp?: boolean;
  translationEnabled?: boolean;
  driverTranslationEnabled?: boolean;
  firstTranslatedOutboundSent?: boolean;
  hasDriverReplied?: boolean;
}

export enum IMessageStatus {
  DELIVERED = 'delivered',
  FAILED = 'failed',
  SENT = 'sent',
  RECEIVED = 'received'
}

export interface IMessageThreadMessageFile {
  name: string;
  mimeType: AllowedMessageThreadMediaMimeTypes;
  url: string;
  size: number;
}

export enum AllowedMessageThreadMediaMimeTypes {
  JPG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  HEIC = 'image/heic',
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  CSV = 'text/csv',
  TSV = 'text/tab-separated-values'
}

export enum MessageSourceType {
  DRIVER = 'driver',
  WAREHOUSE = 'warehouse',
  SYSTEM = 'system'
}

export enum MessageTranslationStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  FAILED = 'failed'
}

export interface IMessageThreadMessageTranslation extends INovaEntity {
  orgId: string;
  messageThreadMessageId: string;
  targetLanguageTag: string;
  content: string;
  confidence: Nullable<number>;
  provider: Nullable<string>;
}

export interface IMessageThreadMessageTranslationResponse {
  targetLanguageTag: string;
  content: string;
  confidence?: Nullable<number>;
  provider?: Nullable<string>;
}

export const MessageThreadMessageTranslationNotes = {
  ENABLED: 'Translation is enabled.',
  UNAVAILABLE: 'Translation unavailable.'
} as const;

export interface IMessageThreadMessage {
  providerMessageId: string;
  orgId?: string;
  messageThreadId: string;
  timestamp?: Date;
  content?: Maybe<string>;
  files?: IMessageThreadMessageFile[];
  toNumber: string;
  fromNumber: string;
  status: IMessageStatus;
  createdBy?: Nullable<string>;
  sourceType?: MessageSourceType;
  translationStatus?: Nullable<MessageTranslationStatus>;
  sourceLanguageTag?: Nullable<string>;
  sourceLanguageConfidence?: Nullable<number>;
}

export interface IMessageThreadEvent {
  orgId: string;
  messageThreadId: string;
  content: string;
}

export function isMessageThreadExpired(messageThread: IMessageThread): boolean {
  const expiresAtUTC = new Date(messageThread.expiresAt).getTime();
  const nowUTC = new Date().getTime();
  return nowUTC > expiresAtUTC;
}

enum SubscriptionKeyword {
  START = 'start',
  RECIBIR = 'recibir',
  RECEBER = 'receber'
}

enum UnsubscriptionKeyword {
  STOP = 'stop',
  ALTO = 'alto',
  PARAR = 'parar'
}

export function isDriverSubscribing(content: string): boolean {
  return Object.values(SubscriptionKeyword).includes(content?.toLowerCase() as SubscriptionKeyword);
}

export function isDriverUnsubscribing(content: string): boolean {
  return Object.values(UnsubscriptionKeyword).includes(
    content?.toLowerCase() as UnsubscriptionKeyword
  );
}

/**
 * Sanitizes a Twilio/WhatsApp phone number by removing the `whatsapp:` prefix
 * and any other non-phone characters.
 */
export function sanitizeTwilioWhatsAppPhoneNumber(phoneNumber: string): string {
  // Only `+` and `0-9` characters remain
  return phoneNumber.replace(/[^0-9+]/gu, '');
}

/** ISO 3166 alpha-3 codes (Nova filtered list) where driver INFO greetings use Spanish. */
const spanishDriverInfoGreetingCountries = new Set<string>([
  'ARG',
  'BOL',
  'CHL',
  'COL',
  'CRI',
  'CUB',
  'DOM',
  'ECU',
  'ESP',
  'GTM',
  'HND',
  'MEX',
  'NIC',
  'PAN',
  'PER',
  'PRI',
  'PRY',
  'SLV',
  'URY',
  'VEN'
]);

/** True when {@link SystemMessages.INFO} driver greetings use Spanish for this warehouse country. */
export function isSpanishDriverInfoGreetingCountry(country?: string): boolean {
  return Boolean(country && spanishDriverInfoGreetingCountries.has(country));
}

type DriverInfoGreetingLocale = 'en' | 'es' | 'pt';

function driverInfoGreetingLocale(country?: string): DriverInfoGreetingLocale {
  if (country === 'BRA') {
    return 'pt';
  }
  if (isSpanishDriverInfoGreetingCountry(country)) {
    return 'es';
  }
  return 'en';
}

function driverInfoGreetings(
  warehouseName: string,
  locale: DriverInfoGreetingLocale
): { greeting: string; interactiveGreeting: string } {
  switch (locale) {
    case 'pt':
      return {
        greeting: `Obrigado por seu check-in em ${warehouseName}. Instruções serão enviadas para este número. Responda PARAR caso não queira receber mais mensagens.`,
        interactiveGreeting: `Obrigado por seu check-in em ${warehouseName}. Responda OK para receber instruções do armazém. Responda PARAR caso não queira receber mais mensagens.`
      };
    case 'es':
      return {
        greeting: `Gracias por registrarte en ${warehouseName}. Las instrucciones serán enviadas a este número. Responde ALTO si no deseas recibir más mensajes.`,
        interactiveGreeting: `Gracias por registrarte en ${warehouseName}. Responde OK para recibir las instrucciones del almacén. Responde STOP si no deseas recibir más mensajes.`
      };
    default:
      return {
        greeting: `Thanks for checking in at ${warehouseName}. Instructions will follow to this phone number. Reply STOP to unsubscribe.`,
        interactiveGreeting: `Thanks for checking in at ${warehouseName}. Reply OK to receive instructions from the warehouse. Reply STOP to unsubscribe.`
      };
  }
}

export const SystemMessages = {
  ERROR: {
    DEFAULT: () =>
      'Automated Response: An error occurred while processing your message. Please try again later.',
    INVALID_FILE_TYPE: (fileType?: string) =>
      `Automated Response: Sorry, your message was not delivered. ${
        fileType
          ? `The file type ${fileType} is not supported.`
          : 'This file type is not supported.'
      }`,
    THREAD_NOT_FOUND: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. There are no active conversations with ${warehouseName}.`,
    THREAD_CLOSED: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. Conversation with ${warehouseName} has been closed.`,
    THREAD_EXPIRED: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. Conversation with ${warehouseName} has expired.`
  },
  EVENT: {
    DRIVER_SUBSCRIBED: () => 'The driver has opted to start receiving messages',
    DRIVER_UNSUBSCRIBED: () => 'The driver has opted to stop receiving messages',
    DRIVER_STOPPED_TRANSLATION: () => 'The driver has opted to stop translation',
    WAREHOUSE_STOPPED_TRANSLATION: () => 'The warehouse has opted to stop translation',
    WAREHOUSE_RESUMED_TRANSLATION: () => 'The warehouse has opted to resume translation',
    PHONE_CHANGED: (phone: string) => `Driver's phone changed to ${formatPhoneNumber(phone)}`,
    THREAD_OPEN: () => 'Chat resumed by the warehouse',
    THREAD_CLOSED: () => 'Chat ended by the warehouse',
    THREAD_CLOSED_BY_ANOTHER_CHECKIN: (warehouseName: string) =>
      `Chat at ${warehouseName} ended due to another check-in`
  },
  INFO: {
    DRIVER_GREETING: (warehouseName: string, country?: string) =>
      driverInfoGreetings(warehouseName, driverInfoGreetingLocale(country)).greeting,
    DRIVER_INTERACTIVE_GREETING: (warehouseName: string, country?: string) =>
      driverInfoGreetings(warehouseName, driverInfoGreetingLocale(country)).interactiveGreeting,
    APPOINTMENT_IN_PROGRESS: (warehouseName: string, dockName: string) =>
      `Your appointment at ${warehouseName} is now in progress. You have been assigned to dock ${dockName}.`,
    APPOINTMENT_COMPLETED: (warehouseName: string) =>
      `Your appointment at ${warehouseName} has been completed.`
  }
};
